import { resetApiTokenCache } from '../api/withApiAuth';
import { clearBrowserAuthCookiesBestEffort, postAuthLogout } from '../auth/authLogout';
import { flushLocalFavoriteAssetsToServer } from '../favorites/favoriteAssetsServerPush';
import { LANGUAGE_STORAGE_KEY } from '../i18n/languages';
import { createDefaultSnapshot, snapshotToUrl } from '../navigation/appHistory';
import {
  shouldPreserveAppLocalKey,
  type AppLocalStoragePreservationOptions,
} from './appLocalStoragePreservation';
import { clearHomeRankingPoolCache } from './homeAssetSearch';

const APP_LOCAL_STORAGE_PREFIX = 'ofrontend.';
const APP_LOCAL_STORAGE_LEGACY_PREFIX = 'ofrontendProfile';

export type ClearAllAppLocalDataOptions = AppLocalStoragePreservationOptions;

export function isAppLocalStorageKey(key: string): boolean {
  return key.startsWith(APP_LOCAL_STORAGE_PREFIX) || key.startsWith(APP_LOCAL_STORAGE_LEGACY_PREFIX);
}

function clearMatchingStorageKeys(storage: Storage, options?: ClearAllAppLocalDataOptions): void {
  const keys: string[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (key && isAppLocalStorageKey(key) && !shouldPreserveAppLocalKey(key, options)) {
      keys.push(key);
    }
  }
  for (const key of keys) {
    storage.removeItem(key);
  }
}

export async function clearServiceWorkerAndCaches(): Promise<void> {
  if (typeof caches !== 'undefined') {
    try {
      const cacheKeys = await caches.keys();
      await Promise.all(cacheKeys.map((key) => caches.delete(key)));
    } catch {
      /* ignore */
    }
  }
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    } catch {
      /* ignore */
    }
  }
}

/**
 * Wipe Placy client data on this browser: session tokens, rebuildable caches,
 * service worker registration, and reachable auth cookies. User drafts (favourites,
 * booking profile, asset-create attempts, profile hints, etc.) stay on the device.
 */
export async function clearAllAppLocalData(options?: ClearAllAppLocalDataOptions): Promise<void> {
  const preserveLanguage = options?.preserveLanguage !== false;
  let savedLanguage: string | null = null;

  if (preserveLanguage && typeof localStorage !== 'undefined') {
    try {
      savedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    } catch {
      savedLanguage = null;
    }
  }

  try {
    await flushLocalFavoriteAssetsToServer();
  } catch {
    /* best effort — still wipe local cache below */
  }

  if (typeof localStorage !== 'undefined') {
    clearMatchingStorageKeys(localStorage, options);
  }
  if (typeof sessionStorage !== 'undefined') {
    clearMatchingStorageKeys(sessionStorage, options);
  }

  if (preserveLanguage && savedLanguage && typeof localStorage !== 'undefined') {
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, savedLanguage);
    } catch {
      /* ignore */
    }
  }

  clearHomeRankingPoolCache();
  resetApiTokenCache();
  clearBrowserAuthCookiesBestEffort();
  await postAuthLogout();
  await clearServiceWorkerAndCaches();
}

/** Reload on the login screen after clear so React state and bundles start fresh. */
export async function clearAllAppLocalDataAndReload(options?: ClearAllAppLocalDataOptions): Promise<void> {
  await clearAllAppLocalData(options);
  if (typeof window !== 'undefined') {
    window.location.replace(snapshotToUrl(createDefaultSnapshot(false)));
  }
}
